<script setup>
import { ChevronRightIcon } from '@heroicons/vue/20/solid';
import NavigationLink from './NavigationLink.vue';
import { focusRing } from './navigationClasses.js';

defineProps({
    item: {
        type: Object,
        required: true,
    },
});
</script>

<template>
    <!--
        The panel is named after the trigger that discloses it, and a plain
        `div` is `role="generic"`, for which a name is prohibited — browsers
        drop it. `group` is the neutral role that carries one.
    -->
    <!--
        Figma builds the panel from its `GLASS` effect (radius 8, refraction
        0.8, lightAngle 60°), which CSS cannot express. `backdrop-filter` covers
        the blur alone, so the 12px and the inset light edge stand in for the
        refraction that dissolves the header edge behind the panel.

        Glass also lets the backdrop read through far stronger than compositing
        its 90% white fill does — at 12px the remaining tenth averages out to an
        even surface. 80% brings the transparency back; over a black backdrop
        that still leaves the label at 5.9:1. Border and drop shadow are the
        Figma values.
    -->
    <div
        role="group"
        class="w-70 bg-white/80 backdrop-blur-md border border-deep-teal-200 purple-theme:border-purple-200 rounded-xl rounded-br-[4rem] shadow-[0_0_8px] shadow-deep-teal-700/25 purple-theme:shadow-purple-700/25 inset-shadow-[0_1px_0] inset-shadow-white/60 p-6"
    >
        <ul class="space-y-4 text-base text-deep-teal-700 purple-theme:text-purple-700">
            <li>
                <!--
                    The section's own page, one step above the entries below
                    it — Figma sets it apart at 18/28 and adds a chevron. Both
                    are unconditional; only the weight carries a state. It is
                    labelled "Übersicht" rather than with the page title, which
                    the trigger above already shows.

                    Being on that page is what `aria-current="page"` already
                    announces; semibold is its visual counterpart, matching
                    how the entries below mark the current one.

                    The chevron sits inside the link, hence the `group`
                    variants: they keep the underline on the label instead of
                    dragging it under the icon.
                -->
                <NavigationLink
                    :item
                    class="group inline-flex items-center rounded-xs text-lg"
                    :class="[focusRing, { 'font-semibold': item.is_current }]"
                >
                    <span class="underline-offset-4 group-hover:underline group-focus-visible:underline">{{ $t('components.navigation.overview') }}</span>

                    <ChevronRightIcon
                        class="size-5 shrink-0"
                        aria-hidden="true"
                    />
                </NavigationLink>
            </li>

            <li
                v-for="child in item.children"
                :key="child.permalink"
            >
                <NavigationLink
                    :item="child"
                    class="block border-l-2 rounded-r-xs pl-3 underline-offset-4 hover:underline focus-visible:underline transition-colors"
                    :class="[
                        focusRing,
                        child.is_current
                            ? 'font-semibold border-deep-teal-700 purple-theme:border-purple-700'
                            : 'border-deep-teal-200 purple-theme:border-purple-200',
                    ]"
                />
            </li>
        </ul>
    </div>
</template>
